/**
 * Centralized query keys and cache invalidation strategy
 *
 * This module provides:
 * 1. All query keys in one place for consistency
 * 2. Dependency map for cascade invalidation
 * 3. Stale time configurations by data type
 * 4. invalidateRelated() helper for cascade invalidation via INVALIDATION_MAP
 */
import type { QueryClient } from '@tanstack/react-query';

// =========================================
// Query Keys
// =========================================

export const QUERY_KEYS = {
  // Contractors
  CONTRACTORS: 'contractors',

  // Branches
  BRANCHES: 'branches',
  BRANCHES_LIST: 'branches-list',
  MY_BRANCH: 'my-branch',

  // Plans
  PLANS: 'plans',
  PLANS_BY_BRANCH: 'plans-by-branch',
  CONTRACTOR_CHANNELS_OPTIONS: 'contractor-channels-options',
  BRANCHES_OPTIONS: 'branches-options',
  AVAILABLE_CONTRACTORS_FOR_PLAN: 'available-contractors-for-plan',

  // Dashboard - Traffic Manager
  TM_DASHBOARD: 'tm-dashboard',
  TM_DASHBOARD_BY_BRANCH: 'tm-dashboard-by-branch',
  TM_SUMMARY: 'tm-summary',
  TM_CONTRACTOR_VIEW: 'tm-contractor-view',
  TM_CONTRACTOR_SUMMARY: 'tm-contractor-summary',
  TM_CONTRACTOR_CHANNELS: 'tm-contractor-channels',
  TM_BRANCH_CARD: 'tm-branch-card',

  // Dashboard - Contractor
  CONTRACTOR_DASHBOARD: 'contractor-dashboard',
  CONTRACTOR_SUMMARY: 'contractor-summary',
  CONTRACTOR_CHANNELS: 'contractor-channels',

  // Dashboard - Branch
  BRANCH_CARD: 'branch-card',
  BRANCH_PNL: 'branch-pnl',
  BRANCH_PLANS: 'branch-plans',

  // Payments
  PAYMENTS: 'payments',
  PAYMENTS_TM: 'payments-tm',
  PAYMENTS_CONTRACTOR: 'payments-contractor',
  PAYMENTS_BRANCH: 'payments-branch',
  PAYMENT_INDICATORS: 'payment-indicators',
  PAYMENT_FILES: 'payment-files',
  PAYMENT_NOTES: 'payment-notes',

  // Reports
  DAILY_REPORTS: 'daily-reports',

  // Organic sources
  ORGANIC_SOURCES: 'organic-sources',
  ORGANIC_PERFORMANCE: 'organic-performance',

  // Traffic channels
  TRAFFIC_CHANNELS: 'traffic-channels',

  // Forecasts
  FORECASTS: 'forecasts',

  // Account managers
  ACCOUNT_MANAGERS: 'account-managers',
  AM_DASHBOARD: 'am-dashboard',

  // Plan approval
  PLAN_APPROVAL: 'plan-approval',

  // Payment settings
  PAYMENT_SETTINGS: 'payment-settings',

  // API clients
  API_CLIENTS: 'api-clients',

  // API call logs (история входящих write-вызовов)
  API_CALL_LOGS: 'api-call-logs',

  // Атрибуты заявок: каталог против фактически приходящих ключей
  LEAD_ATTRIBUTES: 'lead-attributes',

  // Notifications (инбокс)
  NOTIFICATIONS: 'notifications',
  NOTIFICATIONS_UNREAD: 'notifications-unread',

  // Account settings
  ACCOUNT_NOTIFICATION_SETTINGS: 'account-notification-settings',

  // Рассылки
  MAILING_BRANCHES: 'mailing-branches',
  MAILING_MACROS: 'mailing-macros',
  MAILING_HISTORY: 'mailing-history',

  // Баланс предоплатных каналов
  BALANCE: 'balance',

  // CRM (сквозная аналитика)
  CRM_SETTINGS: 'crm-settings',
  CRM_ENABLED: 'crm-enabled',
  CRM_STATUSES: 'crm-statuses',
  CROSS_ANALYTICS: 'cross-analytics',
  BRANCH_PERIOD_ANALYTICS: 'branch-period-analytics',

  // Интеграция с Яндексом (Директ + Метрика)
  YANDEX_SETTINGS: 'yandex-settings',
  YANDEX_INTEGRATION: 'yandex-integration',
  YANDEX_APPS: 'yandex-apps',
  YANDEX_CONNECTIONS: 'yandex-connections',
  YANDEX_AUTH_SESSION: 'yandex-auth-session',
  YANDEX_ANALYTICS: 'yandex-analytics',
  YANDEX_ATTRIBUTION_BREAKDOWN: 'yandex-attribution-breakdown',
  YANDEX_NODE_LEADS: 'yandex-node-leads',
  YANDEX_SYNC_STATUS: 'yandex-sync-status',
  // Отчёт «Все источники» и сопоставления меток каналам
  YANDEX_SOURCES_ANALYTICS: 'yandex-sources-analytics',
  YANDEX_SOURCES_NODE_LEADS: 'yandex-sources-node-leads',
  YANDEX_SOURCE_RULES: 'yandex-source-rules',
} as const;

export type QueryKeyType = (typeof QUERY_KEYS)[keyof typeof QUERY_KEYS];

// =========================================
// Cache Invalidation Dependency Map
// =========================================

/**
 * Maps primary keys to all keys that should be invalidated together
 * When a mutation affects one entity, related caches need updates
 *
 * Example: Creating a contractor affects dashboard summaries
 */
export const INVALIDATION_MAP: Record<string, string[]> = {
  // Contractor changes affect dashboards and plans
  [QUERY_KEYS.CONTRACTORS]: [
    QUERY_KEYS.CONTRACTORS,
    QUERY_KEYS.TM_DASHBOARD,
    QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
    QUERY_KEYS.TM_SUMMARY,
    QUERY_KEYS.TM_CONTRACTOR_CHANNELS,
    QUERY_KEYS.CONTRACTOR_CHANNELS_OPTIONS,
  ],

  // Branch changes affect dashboards and plans
  [QUERY_KEYS.BRANCHES]: [
    QUERY_KEYS.BRANCHES,
    QUERY_KEYS.BRANCHES_LIST,
    QUERY_KEYS.TM_DASHBOARD,
    QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
    QUERY_KEYS.TM_SUMMARY,
    QUERY_KEYS.BRANCHES_OPTIONS,
    // Активация/деактивация филиала создаёт или гасит уведомления о балансе
    QUERY_KEYS.NOTIFICATIONS,
    QUERY_KEYS.NOTIFICATIONS_UNREAD,
  ],

  // Plan changes affect dashboards and payments
  [QUERY_KEYS.PLANS]: [
    QUERY_KEYS.PLANS,
    QUERY_KEYS.PLANS_BY_BRANCH,
    QUERY_KEYS.TM_DASHBOARD,
    QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
    QUERY_KEYS.TM_SUMMARY,
    QUERY_KEYS.CONTRACTOR_DASHBOARD,
    QUERY_KEYS.CONTRACTOR_SUMMARY,
    QUERY_KEYS.BRANCH_CARD,
    QUERY_KEYS.PAYMENTS,
    QUERY_KEYS.AVAILABLE_CONTRACTORS_FOR_PLAN,
  ],

  // Plans by branch changes
  [QUERY_KEYS.PLANS_BY_BRANCH]: [
    QUERY_KEYS.PLANS_BY_BRANCH,
    QUERY_KEYS.PLANS,
    QUERY_KEYS.TM_DASHBOARD,
    QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
    QUERY_KEYS.TM_SUMMARY,
    QUERY_KEYS.AVAILABLE_CONTRACTORS_FOR_PLAN,
  ],

  // Payment changes affect dashboards and indicators.
  // Оплата/сумма счёта «Оплата работы» меняет баланс → уведомления о балансе,
  // поэтому каскад обновляет и инбокс (бейдж — сразу, без ожидания поллинга).
  [QUERY_KEYS.PAYMENTS]: [
    QUERY_KEYS.PAYMENTS,
    QUERY_KEYS.PAYMENTS_TM,
    QUERY_KEYS.PAYMENTS_CONTRACTOR,
    QUERY_KEYS.PAYMENTS_BRANCH,
    QUERY_KEYS.PAYMENT_INDICATORS,
    QUERY_KEYS.TM_DASHBOARD,
    QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
    QUERY_KEYS.TM_SUMMARY,
    QUERY_KEYS.TM_BRANCH_CARD,
    QUERY_KEYS.CONTRACTOR_DASHBOARD,
    QUERY_KEYS.CONTRACTOR_SUMMARY,
    QUERY_KEYS.BALANCE,
    QUERY_KEYS.NOTIFICATIONS,
    QUERY_KEYS.NOTIFICATIONS_UNREAD,
  ],

  // TM payment changes
  [QUERY_KEYS.PAYMENTS_TM]: [
    QUERY_KEYS.PAYMENTS_TM,
    QUERY_KEYS.PAYMENTS,
    QUERY_KEYS.PAYMENT_INDICATORS,
    QUERY_KEYS.TM_DASHBOARD,
    QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
    QUERY_KEYS.TM_BRANCH_CARD,
    QUERY_KEYS.BALANCE,
    QUERY_KEYS.NOTIFICATIONS,
    QUERY_KEYS.NOTIFICATIONS_UNREAD,
  ],

  // Contractor payment changes
  [QUERY_KEYS.PAYMENTS_CONTRACTOR]: [
    QUERY_KEYS.PAYMENTS_CONTRACTOR,
    QUERY_KEYS.PAYMENTS,
    QUERY_KEYS.PAYMENT_INDICATORS,
    QUERY_KEYS.CONTRACTOR_DASHBOARD,
    QUERY_KEYS.BALANCE,
    QUERY_KEYS.NOTIFICATIONS,
    QUERY_KEYS.NOTIFICATIONS_UNREAD,
  ],

  // Branch payment changes
  [QUERY_KEYS.PAYMENTS_BRANCH]: [
    QUERY_KEYS.PAYMENTS_BRANCH,
    QUERY_KEYS.PAYMENTS,
    QUERY_KEYS.PAYMENT_INDICATORS,
    QUERY_KEYS.BRANCH_CARD,
    QUERY_KEYS.BALANCE,
    QUERY_KEYS.NOTIFICATIONS,
    QUERY_KEYS.NOTIFICATIONS_UNREAD,
  ],

  // Daily reports affect dashboards.
  // Ручная правка лидов/«Оплаты работы» меняет баланс → обновляем и инбокс.
  [QUERY_KEYS.DAILY_REPORTS]: [
    QUERY_KEYS.DAILY_REPORTS,
    QUERY_KEYS.TM_DASHBOARD,
    QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
    QUERY_KEYS.TM_SUMMARY,
    QUERY_KEYS.CONTRACTOR_DASHBOARD,
    QUERY_KEYS.CONTRACTOR_SUMMARY,
    QUERY_KEYS.BRANCH_CARD,
    QUERY_KEYS.BALANCE,
    QUERY_KEYS.NOTIFICATIONS,
    QUERY_KEYS.NOTIFICATIONS_UNREAD,
  ],

  // Traffic channel changes
  [QUERY_KEYS.TRAFFIC_CHANNELS]: [
    QUERY_KEYS.TRAFFIC_CHANNELS,
    QUERY_KEYS.TM_DASHBOARD,
    QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
    QUERY_KEYS.CONTRACTOR_CHANNELS,
    QUERY_KEYS.TM_CONTRACTOR_CHANNELS,
  ],

  // Organic sources
  [QUERY_KEYS.ORGANIC_SOURCES]: [
    QUERY_KEYS.ORGANIC_SOURCES,
    QUERY_KEYS.ORGANIC_PERFORMANCE,
    QUERY_KEYS.TM_DASHBOARD,
    QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
  ],

  // Forecasts
  [QUERY_KEYS.FORECASTS]: [
    QUERY_KEYS.FORECASTS,
    QUERY_KEYS.CONTRACTOR_DASHBOARD,
    QUERY_KEYS.CONTRACTOR_SUMMARY,
  ],

  // Plan approval
  [QUERY_KEYS.PLAN_APPROVAL]: [
    QUERY_KEYS.PLAN_APPROVAL,
    QUERY_KEYS.BRANCH_PLANS,
    QUERY_KEYS.PLANS,
    QUERY_KEYS.TM_DASHBOARD,
    QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
    QUERY_KEYS.PAYMENTS_TM,
    QUERY_KEYS.PAYMENTS,
    QUERY_KEYS.PAYMENT_INDICATORS,
  ],

  // Payment settings
  [QUERY_KEYS.PAYMENT_SETTINGS]: [
    QUERY_KEYS.PAYMENT_SETTINGS,
    QUERY_KEYS.PAYMENTS,
  ],

  // API clients
  [QUERY_KEYS.API_CLIENTS]: [QUERY_KEYS.API_CLIENTS],

  // Account managers
  [QUERY_KEYS.ACCOUNT_MANAGERS]: [
    QUERY_KEYS.ACCOUNT_MANAGERS,
    QUERY_KEYS.BRANCHES,
  ],

  [QUERY_KEYS.MAILING_BRANCHES]: [QUERY_KEYS.MAILING_BRANCHES],
  [QUERY_KEYS.MAILING_MACROS]: [
    QUERY_KEYS.MAILING_MACROS,
    QUERY_KEYS.MAILING_HISTORY,
  ],
  [QUERY_KEYS.MAILING_HISTORY]: [QUERY_KEYS.MAILING_HISTORY],

  // Баланс (ручная корректировка) отражается в карточках и списке платежей ТМ
  [QUERY_KEYS.BALANCE]: [
    QUERY_KEYS.BALANCE,
    QUERY_KEYS.BRANCH_CARD,
    QUERY_KEYS.TM_BRANCH_CARD,
    QUERY_KEYS.PAYMENTS_TM,
    // Ручная корректировка баланса может создать/погасить уведомление
    QUERY_KEYS.NOTIFICATIONS,
    QUERY_KEYS.NOTIFICATIONS_UNREAD,
  ],
};

// =========================================
// Stale Time Configuration
// =========================================

/**
 * Stale times by data type
 * - STATIC: Data that rarely changes (traffic channels, options)
 * - SEMI_STATIC: Data that changes occasionally (branches, contractors)
 * - DYNAMIC: Data that changes frequently (dashboards, reports)
 * - REALTIME: Data that needs to be fresh (payments, indicators)
 */
export const STALE_TIMES = {
  // Static data (rarely changes) - 30 minutes
  STATIC: 30 * 60 * 1000,

  // Semi-static data (changes occasionally) - 10 minutes
  SEMI_STATIC: 10 * 60 * 1000,

  // Dynamic data (changes frequently) - 1 minute
  DYNAMIC: 60 * 1000,

  // Realtime data (needs to be fresh) - 30 seconds
  REALTIME: 30 * 1000,
} as const;

/**
 * Mapping of query keys to their appropriate stale times
 */
export const QUERY_STALE_CONFIG: Partial<Record<QueryKeyType, number>> = {
  // Static
  [QUERY_KEYS.TRAFFIC_CHANNELS]: STALE_TIMES.STATIC,
  [QUERY_KEYS.CONTRACTOR_CHANNELS_OPTIONS]: STALE_TIMES.STATIC,
  [QUERY_KEYS.BRANCHES_OPTIONS]: STALE_TIMES.STATIC,

  // Semi-static
  [QUERY_KEYS.BRANCHES]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.BRANCHES_LIST]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.MY_BRANCH]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.CONTRACTORS]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.ORGANIC_SOURCES]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.ACCOUNT_MANAGERS]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.PAYMENT_SETTINGS]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.API_CLIENTS]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.ACCOUNT_NOTIFICATION_SETTINGS]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.MAILING_BRANCHES]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.CRM_SETTINGS]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.CRM_ENABLED]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.CRM_STATUSES]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.YANDEX_SETTINGS]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.YANDEX_INTEGRATION]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.YANDEX_APPS]: STALE_TIMES.SEMI_STATIC,
  [QUERY_KEYS.YANDEX_CONNECTIONS]: STALE_TIMES.DYNAMIC,
  [QUERY_KEYS.YANDEX_SYNC_STATUS]: STALE_TIMES.DYNAMIC,
  [QUERY_KEYS.YANDEX_SOURCE_RULES]: STALE_TIMES.SEMI_STATIC,
  // Разбор привязки меняется вместе с ночным каскадом, не чаще.
  [QUERY_KEYS.YANDEX_ATTRIBUTION_BREAKDOWN]: STALE_TIMES.SEMI_STATIC,

  // Realtime-ish: write-ивенты для мониторинга
  [QUERY_KEYS.API_CALL_LOGS]: STALE_TIMES.REALTIME,
  [QUERY_KEYS.LEAD_ATTRIBUTES]: STALE_TIMES.REALTIME,

  // Dynamic
  [QUERY_KEYS.PLANS]: STALE_TIMES.DYNAMIC,
  [QUERY_KEYS.PLANS_BY_BRANCH]: STALE_TIMES.DYNAMIC,
  [QUERY_KEYS.FORECASTS]: STALE_TIMES.DYNAMIC,
  [QUERY_KEYS.DAILY_REPORTS]: STALE_TIMES.DYNAMIC,
  [QUERY_KEYS.MAILING_MACROS]: STALE_TIMES.DYNAMIC,
  // Сквозная аналитика: бэкенд сам делает фоновый TTL-refresh, кэш можно
  // держать «свежим» минуту, чтобы не дёргать выборку при каждом ре-рендере.
  [QUERY_KEYS.CROSS_ANALYTICS]: STALE_TIMES.DYNAMIC,
  [QUERY_KEYS.BRANCH_PERIOD_ANALYTICS]: STALE_TIMES.DYNAMIC,

  // Realtime
  [QUERY_KEYS.TM_DASHBOARD]: STALE_TIMES.REALTIME,
  [QUERY_KEYS.TM_DASHBOARD_BY_BRANCH]: STALE_TIMES.REALTIME,
  [QUERY_KEYS.TM_SUMMARY]: STALE_TIMES.REALTIME,
  [QUERY_KEYS.CONTRACTOR_DASHBOARD]: STALE_TIMES.REALTIME,
  [QUERY_KEYS.CONTRACTOR_SUMMARY]: STALE_TIMES.REALTIME,
  [QUERY_KEYS.BRANCH_CARD]: STALE_TIMES.REALTIME,
  [QUERY_KEYS.PAYMENTS]: STALE_TIMES.REALTIME,
  [QUERY_KEYS.PAYMENT_INDICATORS]: STALE_TIMES.REALTIME,
  [QUERY_KEYS.BALANCE]: STALE_TIMES.REALTIME,
  [QUERY_KEYS.MAILING_HISTORY]: STALE_TIMES.REALTIME,
};

/**
 * Helper function to get stale time for a query key
 */
export function getStaleTime(key: QueryKeyType): number {
  return QUERY_STALE_CONFIG[key] ?? STALE_TIMES.DYNAMIC;
}

/**
 * Cascade invalidation helper.
 * Looks up each key in INVALIDATION_MAP and invalidates all related queries.
 * Keys not found in the map are invalidated directly.
 */
export function invalidateRelated(
  queryClient: QueryClient,
  ...keys: string[]
): void {
  const toInvalidate = new Set<string>();
  for (const key of keys) {
    const related = INVALIDATION_MAP[key];
    if (related) {
      related.forEach((k) => toInvalidate.add(k));
    } else {
      toInvalidate.add(key);
    }
  }
  for (const key of toInvalidate) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
}
